import type { NextStatusPhaseResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchExitOptionsApi } from "../../../api/client";
import { exitOptionsKeys } from "./exitOptionsKeys";

/**
 * Shared queryOptions for the Workbench exit-screen dropdown options (key +
 * queryFn). Backed by the generated WorkbenchExitOptionsApi.getNextStatusPhases,
 * which forwards to recap-service and returns the next-status / phase options
 * (`nextStatusList`, `phasesList`) plus the current status/phase for the deal.
 *
 * All three inputs are required by the BFF, so the query stays disabled until
 * `approvalId`, `currentStatus`, and `roleName` are present (and the caller
 * opts in via `enabled`) — this keeps the request from firing before the
 * application-status read resolves or while the modal is closed.
 * @param {string} approvalId - The deal / approval identifier (path param).
 * @param {string} currentStatus - The deal's current status (query param).
 * @param {string} roleName - The analyst's role name (query param).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guards.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function exitOptionsQuery(approvalId: string, currentStatus: string, roleName: string, enabled = true) {
  return queryOptions({
    queryKey: exitOptionsKeys.detail({ approvalId, currentStatus, roleName }),
    queryFn: async ({ signal }): Promise<NextStatusPhaseResponse> => {
      const response = await workbenchExitOptionsApi.getNextStatusPhases(approvalId, currentStatus, roleName, {
        signal,
      });
      return response.data;
    },
    staleTime: 30_000,
    enabled: enabled && !!approvalId && !!currentStatus && !!roleName,
  });
}

/**
 * Reads the next-status / phase options for the exit screen. Disabled until the
 * approval id, current status, and role name are all present and the caller
 * enables it (e.g. only while the exit modal is open).
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} currentStatus - The deal's current status.
 * @param {string} roleName - The analyst's role name.
 * @param {{ enabled?: boolean }} [options] - Caller opt-in (defaults to enabled).
 * @param {boolean} [options.enabled] - When false, the query never fires.
 * @returns {ReturnType<typeof useQuery<NextStatusPhaseResponse>>} The TanStack Query result.
 */
export function useExitOptions(
  approvalId: string,
  currentStatus: string,
  roleName: string,
  options?: { enabled?: boolean }
) {
  return useQuery(exitOptionsQuery(approvalId, currentStatus, roleName, options?.enabled ?? true));
}
